import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  ArrowCounterClockwise,
  PencilSimple,
  Trash,
} from '@phosphor-icons/react';
import { aclHelper } from 'helper';

import { useCreateModal } from 'partial/components/Modal';
import Button from 'partial/components/Button';
import ThumbIcon from 'partial/components/ThumbIcon';
import AddUpdateUser from '../modals/AddUpdateUser';
import DeleteUser from '../modals/DeleteUser';
import ResetPass from '../modals/ResetPass';
import StatusButton from '../components/StatusButton';

import { useUserById } from '../hooks';

const DT = 'text-sm text-neutral-500';
const DD = 'text-sm font-medium text-neutral-900';

const UserDetails = ({ users }) => {
  const { id } = useParams();
  const createModal = useCreateModal();
  const [isLoading, userDetails] = useUserById(id);

  const firstName = userDetails?.profile?.first_name || '--';
  const lastName = userDetails?.profile?.last_name || '--';
  const email = userDetails?.email || '--';

  const fullName = `${firstName} ${lastName}`;

  const handleEditUser = useCallback(() => {
    createModal({
      content: (onClose) => (
        <AddUpdateUser type="edit" onClose={onClose} prevData={userDetails} />
      ),
    });
  }, [createModal, userDetails]);

  const handleDeleteUser = useCallback(() => {
    createModal({
      content: (onClose) => (
        <DeleteUser onClose={onClose} uuid={userDetails?.uuid} users={users} />
      ),
    });
  }, [createModal, userDetails, users]);

  const handleResetPass = useCallback(() => {
    createModal({
      content: (onClose) => (
        <ResetPass onClose={onClose} uuid={userDetails?.uuid} />
      ),
    });
  }, [createModal, userDetails]);

  return (
    <div className="flex-grow min-w-0">
      {isLoading ? (
        <div>
          <div className="animate-pulse flex space-x-4 p-6">
            <div className="rounded-full bg-neutral-100 h-24 w-24" />
            <div className="flex-1 space-y-6 py-1">
              <div className="h-8 bg-neutral-100 rounded" />
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-4">
                  <div className="h-8 bg-neutral-100 rounded col-span-2" />
                  <div className="h-8 bg-neutral-100 rounded col-span-1" />
                </div>
                <div className="h-8 bg-neutral-100 rounded" />
                <div className="h-8 bg-neutral-100 rounded" />
                <div className="h-8 bg-neutral-100 rounded" />
                <div className="h-8 bg-neutral-100 rounded" />
                <div className="h-8 bg-neutral-100 rounded" />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="p-6 flex items-start justify-between gap-6 border-b border-neutral-200">
            <figure className="flex items-center gap-4 min-w-0">
              <ThumbIcon
                className="h-[52px] w-[52px] rounded-full"
                initialsClassName="!text-lg"
                alt={fullName}
              />
              <figcaption className="flex flex-col gap-1 min-w-0">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold capitalize text-neutral-900 truncate">
                    {fullName || '--'}
                  </h2>
                  {/* A role is not a status — a neutral chip, not a badge. */}
                  {userDetails?.role?.name && (
                    <span className="inline-flex items-center h-[22px] px-2 rounded border border-neutral-200 bg-neutral-50 text-xs font-semibold text-neutral-700 whitespace-nowrap">
                      {userDetails.role.name}
                    </span>
                  )}
                </div>
                <p className="text-sm text-neutral-500 truncate">
                  {email || '--'}
                </p>
              </figcaption>
            </figure>
            <div className="flex items-center gap-2 flex-shrink-0">
              <StatusButton userDetails={userDetails} />
              {aclHelper(
                'user',
                'u',
                <Button
                  outline
                  icon={PencilSimple}
                  disabled={isLoading}
                  onClick={handleEditUser}
                >
                  Edit
                </Button>,
              )}
              {aclHelper(
                'user',
                'd',
                <Button
                  outline
                  icon={Trash}
                  disabled={isLoading}
                  onClick={handleDeleteUser}
                >
                  Delete
                </Button>,
              )}
            </div>
          </div>
          <div className="p-6">
            <h3 className="text-xs font-semibold uppercase tracking-[.05em] text-neutral-500 mb-4">
              Summary
            </h3>
            <dl className="grid grid-cols-[minmax(0,180px)_1fr] gap-y-3 items-center max-w-2xl">
              <dt className={DT}>First name</dt>
              <dd className={DD}>{firstName}</dd>
              <dt className={DT}>Middle name</dt>
              <dd className={DD}>
                {userDetails?.profile?.middle_name || '--'}
              </dd>
              <dt className={DT}>Last name</dt>
              <dd className={DD}>{lastName}</dd>
              <dt className={DT}>Email address</dt>
              <dd className={DD}>{email}</dd>
              <dt className={DT}>Mobile number</dt>
              <dd className={`${DD} tabular-nums`}>
                {userDetails?.profile?.mobile_number || '--'}
              </dd>
              {aclHelper(
                'user',
                'u',
                <>
                  <dt className={DT}>Password</dt>
                  <dd>
                    <Button
                      outline
                      size="sm"
                      icon={ArrowCounterClockwise}
                      disabled={isLoading}
                      onClick={handleResetPass}
                    >
                      Reset password
                    </Button>
                  </dd>
                </>,
              )}
            </dl>
          </div>
        </>
      )}
    </div>
  );
};

UserDetails.propTypes = {
  users: PropTypes.instanceOf(Array).isRequired,
};

export default UserDetails;
